import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http, NGN } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Copy, Clock, Warning, Bank, DeviceMobile, ArrowRight, HouseSimple, DownloadSimple } from "@phosphor-icons/react";
import { BACKEND_URL } from "@/lib/api";

const HOW = [
  { t: "Open your bank app", d: "Login to your normal bank mobile app or USSD." },
  { t: "Select Transfer", d: "Choose 'Transfer to Other Bank'." },
  { t: "Enter Account Number", d: "Paste the account number shown above." },
  { t: "Enter Amount", d: "Type the exact amount displayed below." },
  { t: "Confirm Transfer", d: "Follow your bank's confirmation steps." },
  { t: "Return to Mayoto", d: "Come back here — we will confirm automatically." },
];

export default function PaymentStatus() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [copied, setCopied] = useState("");
  const nav = useNavigate();
  const pollRef = useRef();

  const fetchInv = async () => {
    try {
      const { data } = await http.get(`/investments/${id}`);
      setInv(data);
      if (data.status === "successful" && pollRef.current) { clearInterval(pollRef.current); }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchInv();
    pollRef.current = setInterval(fetchInv, 5000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(""), 2000);
    } catch { toast.error("Copy failed"); }
  };

  if (!inv) return <div className="container-wide py-24 text-slate-500">Loading…</div>;

  const isPaid = inv.status === "successful";
  const isReview = inv.status === "under_review";

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <section className="mayo-grain">
        <div className="container-wide py-10 max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <Badge className={isPaid ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : isReview ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-[#00b4d8]/10 text-[#00b4d8] hover:bg-[#00b4d8]/10"}>
              STEP {isPaid ? "3" : "2"} OF 3
            </Badge>
            {inv.mock_mode && <Badge className="bg-slate-800 text-white hover:bg-slate-800 hidden">SANDBOX MODE</Badge>}
          </div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-[#0a1128]" data-testid="payment-title">
            {isPaid ? "Payment Received ✓" : isReview ? "Payment Under Review" : "Pay by Bank Transfer"}
          </h1>
          <p className="mt-2 text-slate-600">
            {isPaid ? "Your investment has been successfully recorded." :
             isReview ? "We have received a transfer but need a manual review. Our team will confirm shortly." :
             "Simply transfer the exact amount below from your bank app or USSD."}
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-wide max-w-3xl">
          {/* Summary */}
          <div className="bg-white rounded-3xl border border-slate-200 p-8 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className={`h-12 w-12 rounded-2xl grid place-items-center ${isPaid ? "bg-emerald-500" : "bg-[#0a1128]"}`}>
                {isPaid ? <CheckCircle size={26} weight="fill" className="text-white" /> : <Clock size={22} className="text-[#00b4d8]" weight="bold" />}
              </div>
              <div>
                <p className="text-xs text-slate-500">INVESTMENT</p>
                <p className="font-display font-bold text-xl text-[#0a1128]" data-testid="payment-inv-name">{inv.opportunity_name}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><p className="text-slate-500 text-xs">Amount</p><p className="font-display font-bold text-lg text-[#0a1128]" data-testid="payment-amount">{NGN(inv.amount)}</p></div>
              <div><p className="text-slate-500 text-xs">Duration</p><p className="font-semibold">{inv.duration}</p></div>
              <div><p className="text-slate-500 text-xs">Reference</p><p className="font-mono text-xs">{inv.reference}</p></div>
              <div><p className="text-slate-500 text-xs">Status</p><p className={`font-semibold ${isPaid ? "text-emerald-600" : "text-amber-600"}`} data-testid="payment-status">{inv.status.replace("_"," ").toUpperCase()}</p></div>
            </div>
          </div>

          {!isPaid && (
            <>
              {/* Bank Details Card */}
              <div className="bg-[#0a1128] text-white rounded-3xl p-8 mb-6" data-testid="payment-bank-card">
                <div className="flex items-center gap-2 mb-6">
                  <Bank size={22} className="text-[#00b4d8]" weight="bold" />
                  <p className="font-display font-bold text-xl">Transfer to this account</p>
                </div>

                <div className="space-y-5">
                  <Row label="Bank" value={inv.virtual_bank_name} />
                  <Row label="Account Name" value={inv.virtual_account_name} />
                  <Row label="Account Number" value={inv.virtual_account_number} big onCopy={() => copy(inv.virtual_account_number, "Account number")} copyLabel="Account number" copied={copied === "Account number"} />
                  <Row label="Amount to Transfer" value={NGN(inv.amount)} big onCopy={() => copy(String(inv.amount), "Amount")} copyLabel="Amount" copied={copied === "Amount"} accent />
                </div>

                {inv.mock_mode && (
                  <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4 flex gap-3 hidden">
                    <Warning size={20} className="text-[#d4af37] mt-0.5" weight="bold" />
                    <p className="text-xs text-slate-300">
                      <span className="font-bold text-[#d4af37]">SANDBOX MODE:</span> This is a test virtual account. When Paystack live keys are activated for Mayoto, real Nigerian bank transfers will auto-confirm here. For now, admins can confirm this test transfer from the admin console.
                    </p>
                  </div>
                )}

                <div className="mt-6 flex items-center gap-2 text-sm text-slate-300">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00b4d8] opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#00b4d8]"></span>
                  </span>
                  <span>Waiting for your payment…</span>
                </div>
              </div>

              {/* How to */}
              <div className="bg-white rounded-3xl border border-slate-200 p-8 mb-6">
                <div className="flex items-center gap-2 mb-6">
                  <DeviceMobile size={22} className="text-[#00b4d8]" weight="bold" />
                  <p className="font-display font-bold text-xl text-[#0a1128]">How to Transfer</p>
                </div>
                <ol className="space-y-4">
                  {HOW.map((s, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="h-8 w-8 shrink-0 rounded-full bg-[#00b4d8] text-white font-display font-black text-sm grid place-items-center">{i + 1}</span>
                      <div>
                        <p className="font-display font-bold text-[#0a1128]">{s.t}</p>
                        <p className="text-sm text-slate-600">{s.d}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <p className="mt-6 text-xs text-slate-500">Transfer the <span className="font-semibold">exact</span> amount. If your bank charges a small fee, that fee is on top of the transfer amount — the amount that arrives in the Mayoto account should be exactly {NGN(inv.amount)}.</p>
              </div>
            </>
          )}

          {isPaid && (
            <div className="bg-white rounded-3xl border border-slate-200 p-8">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-slate-500 text-xs">Transaction Reference</p><p className="font-semibold font-mono text-[#0a1128]">{inv.reference}</p></div>
                <div><p className="text-slate-500 text-xs">Payment Date</p><p className="font-semibold">{new Date(inv.paid_at || inv.created_at).toLocaleString()}</p></div>
                <div><p className="text-slate-500 text-xs">Investment Status</p><p className="font-semibold text-emerald-600">ACTIVE</p></div>
                <div><p className="text-slate-500 text-xs">Return Structure</p><p className="font-semibold">{inv.return_structure}</p></div>
              </div>
              <div className="mt-6 flex gap-3 flex-wrap">
                <Link to="/dashboard" className="flex-1 min-w-[180px]"><Button className="w-full btn-shine bg-[#00b4d8] hover:bg-[#0096c7] rounded-full py-6" data-testid="payment-goto-dashboard"><HouseSimple size={18} className="mr-2" />Go to Dashboard</Button></Link>
                <a href={`${BACKEND_URL}/api/investments/${inv.id}/statement`} download className="flex-1 min-w-[180px]">
                  <Button variant="outline" className="w-full rounded-full py-6 border-[#0a1128] text-[#0a1128]" data-testid="payment-download-statement">
                    <DownloadSimple size={18} className="mr-2" />Download PDF Statement
                  </Button>
                </a>
                <Link to="/investments" className="flex-1 min-w-[180px]"><Button variant="outline" className="w-full rounded-full py-6 border-slate-300 text-slate-700">Explore More <ArrowRight size={16} className="ml-1" /></Button></Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, big, accent, onCopy, copyLabel, copied }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-xs text-slate-400">{label}</p>
        <p className={`${big ? "font-display font-black text-2xl sm:text-3xl" : "font-semibold"} ${accent ? "text-[#d4af37]" : "text-white"} break-all`}>{value}</p>
      </div>
      {onCopy && (
        <button onClick={onCopy} className="shrink-0 inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full transition-colors" data-testid={`copy-${copyLabel?.replace(/\s+/g,"-").toLowerCase()}`}>
          <Copy size={14} />{copied ? "Copied!" : "Copy"}
        </button>
      )}
    </div>
  );
}
